import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, RotateCcw, Maximize2, Minimize2, X } from 'lucide-react';
import SageChatMessage from './SageChatMessage';
import SageChatInput from './SageChatInput';
import { sageApi } from '../../services/api';
import {
  SS_THREAD_ID,
  SS_RAW_MESSAGES,
  SS_DISPLAY_MESSAGES,
  clearSageSession,
} from '../../utils/sageSession';

const STARTER_QUESTIONS = [
  'What metrics are available?',
  'What is our enrollment funnel right now?',
  'Which merchants have the highest total spend?',
  'How many members are currently enrolled?',
  'What are our key lifecycle KPIs?',
  'Show me monthly spending trends',
];

function readSession(key, fallback) {
  try {
    const s = sessionStorage.getItem(key);
    return s ? JSON.parse(s) : fallback;
  } catch {
    return fallback;
  }
}

function readSessionRaw(key, fallback) {
  try {
    return sessionStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

export default function SageChatPanel({ isOpen, onClose }) {
  const [messages, setMessages] = useState(() => readSession(SS_DISPLAY_MESSAGES, []));
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(() => readSessionRaw(SS_THREAD_ID, null));
  // Raw Anthropic-format history (with tool_use/tool_result blocks) re-sent on
  // every request so the gateway can be stateless. Distinct from `messages`,
  // which is the user-facing render model.
  const [rawMessages, setRawMessages] = useState(() => readSession(SS_RAW_MESSAGES, null));
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef(null);
  // Holds the AbortController for the in-flight streaming request. New Chat
  // aborts it so a late onDone can't resurrect a conversation the user reset.
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Reset to slide-out when panel is closed entirely
  useEffect(() => {
    if (!isOpen) setIsExpanded(false);
  }, [isOpen]);

  // Persist display messages so a page refresh keeps the visible conversation.
  // Skip while a response is streaming to avoid a sessionStorage write on every
  // token chunk; onDone/onError settle the final state and trigger the write.
  useEffect(() => {
    const streaming = messages.some((m) => m.streaming);
    if (streaming) return;
    try {
      if (messages.length > 0) {
        sessionStorage.setItem(SS_DISPLAY_MESSAGES, JSON.stringify(messages));
      } else {
        sessionStorage.removeItem(SS_DISPLAY_MESSAGES);
      }
    } catch {
      // Quota or serialization failure — non-fatal; raw history is the source of truth.
    }
  }, [messages]);

  const handleSend = async (question) => {
    setMessages((prev) => [...prev, { role: 'user', text: question }]);
    setMessages((prev) => [...prev, { role: 'assistant', text: '', streaming: true }]);
    setIsLoading(true);

    // Abort any in-flight request before creating a new one. Without this,
    // a rapid Send -> Send sequence leaves the first request live, and its
    // onDone can write stale history back into sessionStorage after a reset.
    abortControllerRef.current?.abort();
    // Fresh controller per request so New Chat can abort this specific stream.
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      await sageApi.askStream(question, threadId, rawMessages, {
        onToken: (chunk) => {
          // Ignore tokens for a stream that New Chat already aborted. The abort
          // normally makes askStream reject before any further callback fires,
          // but a chunk buffered just before the abort could still arrive; this
          // keeps it from appending onto the freshly reset transcript.
          if (abortController.signal.aborted) return;
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last && last.role === 'assistant') {
              updated[updated.length - 1] = { ...last, text: last.text + chunk };
            }
            return updated;
          });
        },
        onDone: (data) => {
          // Belt-and-braces against the New Chat race: if this request was
          // aborted (New Chat clicked mid-stream), a done event that was already
          // buffered must NOT run — otherwise it would write the old thread id /
          // raw history back into state and sessionStorage, resurrecting the
          // conversation the user just reset. The abort is the primary guard
          // (askStream rejects); this is the last line of defense.
          if (abortController.signal.aborted) return;
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last && last.role === 'assistant') {
              updated[updated.length - 1] = {
                ...last,
                text: data.text || last.text,
                sql: data.sql,
                data: data.data,
                streaming: false,
              };
            }
            return updated;
          });
          if (data.thread_id) {
            setThreadId(data.thread_id);
            try {
              sessionStorage.setItem(SS_THREAD_ID, data.thread_id);
            } catch {
              // non-fatal
            }
          }
          // Persist the client-authoritative history returned by the gateway so
          // it can be re-sent on the next request (and survives a refresh).
          if (data.messages) {
            setRawMessages(data.messages);
            try {
              sessionStorage.setItem(SS_RAW_MESSAGES, JSON.stringify(data.messages));
            } catch {
              // Quota exceeded (very long history) — drop the stored copy so we
              // don't send a truncated/corrupt array next time; the gateway
              // falls back to its in-memory thread store.
              try { sessionStorage.removeItem(SS_RAW_MESSAGES); } catch { /* storage entirely unavailable */ }
            }
          }
          setIsLoading(false);
        },
        onError: (errorMsg, code) => {
          // Two codes require a full reset rather than annotating the last
          // message:
          //   - invalid_history: a rejected history never self-resolves; clear
          //     it so the next send falls back to the legacy in-memory path
          //     instead of re-posting the same bad array and 400-ing forever.
          //   - auth_error: the session expired mid-request. api.js already
          //     cleared sessionStorage; we must ALSO replace the in-memory
          //     transcript, otherwise the persist-on-settle effect re-writes it
          //     to SS_DISPLAY_MESSAGES and the prior user's conversation leaks
          //     into the next login in the same tab.
          if (code === 'invalid_history' || code === 'auth_error') {
            setRawMessages(null);
            setThreadId(null);
            // clearSageSession() removes all three keys (SS_THREAD_ID,
            // SS_RAW_MESSAGES, SS_DISPLAY_MESSAGES) in one call. Dropping
            // SS_DISPLAY_MESSAGES is important: it carries result data rows
            // (PII/PHI) that must not remain in the browser after a reset.
            // On the auth_error path api.js ALSO calls clearSageSession() before
            // firing onError; this second call is intentional (not redundant) --
            // it drops anything the persist-on-settle effect may have written to
            // sessionStorage in the render between the api.js clear and here.
            // Do not remove either call.
            clearSageSession();
            // Replace the whole transcript with a single notice. Keeping the
            // prior messages in state would let the persist-on-settle effect
            // immediately re-write the data rows back into SS_DISPLAY_MESSAGES,
            // defeating the clear above. A lone notice carries no PII.
            setMessages([{
              role: 'assistant',
              text: code === 'auth_error'
                ? (errorMsg || 'Your session has expired. Please sign in again.')
                : 'Something went wrong. Your conversation has been reset — please try again.',
              isError: true,
            }]);
            setIsLoading(false);
            return;
          }
          const displayText = errorMsg || 'Something went wrong. Please try again.';
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last && last.role === 'assistant') {
              updated[updated.length - 1] = {
                ...last,
                text: displayText,
                streaming: false,
                isError: true,
              };
            }
            return updated;
          });
          setIsLoading(false);
        },
      }, abortController.signal);
    } catch (err) {
      // Intentional abort (e.g. New Chat mid-stream, or rapid re-send): bail
      // silently. The transcript is already cleared or the user reset
      // deliberately — surfacing "Failed to connect" would be misleading.
      if (err.name === 'AbortError') {
        setIsLoading(false);
        return;
      }
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.role === 'assistant') {
          updated[updated.length - 1] = {
            ...last,
            text: 'Failed to connect to SAGE. Please try again.',
            streaming: false,
            isError: true,
          };
        }
        return updated;
      });
      setIsLoading(false);
    } finally {
      // Only clear the ref if it still points at this request; a newer send
      // may have already replaced it.
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  };

  const handleNewChat = () => {
    // Abort any in-flight stream first so its onDone can't write the old
    // thread id / history back into state and sessionStorage after the reset.
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    // Only call clearThread in legacy (in-memory) mode: when rawMessages is set
    // the client-authoritative path was used and the gateway never stored the
    // thread in _threads, so the DELETE would always 404.
    if (threadId && !rawMessages) {
      sageApi.clearThread(threadId).catch(() => {});
    }
    setMessages([]);
    setThreadId(null);
    setRawMessages(null);
    setIsLoading(false);
    // Clear all persisted conversation state so the next question starts fresh.
    clearSageSession();
  };

  const handleClose = () => {
    if (onClose) onClose();
    setIsExpanded(false);
  };

  const showWelcome = messages.length === 0;

  return (
    <>
      {/* Subtle overlay behind full-page mode */}
      {isExpanded && isOpen && (
        <div className="fixed inset-0 z-[49] bg-black/5 transition-opacity duration-300" />
      )}

      <div
        className={
          isExpanded
            ? `fixed inset-0 z-50 bg-white flex flex-col transition-all duration-300 ease-in-out
               ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`
            : `fixed top-0 right-0 z-40 h-full w-full sm:w-[520px] bg-white border-l border-gray-200 shadow-2xl
               flex flex-col transition-transform duration-300 ease-in-out
               ${isOpen ? 'translate-x-0' : 'translate-x-full'}`
        }
      >
        {/* Header */}
        <div className={`flex items-center justify-between py-3 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-white
                         ${isExpanded ? 'px-6' : 'px-4'}`}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center text-sm font-bold">
              S
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Sunny SAGE</h2>
              <p className="text-[10px] text-gray-400 leading-tight">AI Data Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={handleNewChat}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-gray-500
                           hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                New Chat
              </button>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400
                         hover:bg-gray-100 hover:text-gray-600 transition-colors"
              title={isExpanded ? 'Minimize' : 'Expand'}
            >
              {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={handleClose}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400
                         hover:bg-gray-100 hover:text-gray-600 transition-colors"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages area */}
        <div className={`flex-1 overflow-y-auto py-3 ${isExpanded ? 'px-6' : 'px-4'}`}>
          <div className={isExpanded ? 'max-w-3xl mx-auto w-full h-full' : 'h-full'}>
            {showWelcome ? (
              <div className={`flex flex-col items-center justify-center h-full text-center
                              ${isExpanded ? 'py-16' : ''}`}>
                <div className={`rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mb-4
                                ${isExpanded ? 'w-16 h-16' : 'w-14 h-14'}`}>
                  <MessageCircle className={isExpanded ? 'w-8 h-8' : 'w-7 h-7'} />
                </div>
                <h3 className={`font-semibold text-gray-900 mb-1
                               ${isExpanded ? 'text-xl' : 'text-base'}`}>
                  Ask Sunny SAGE
                </h3>
                <p className={`text-gray-400 mb-6
                              ${isExpanded ? 'text-sm max-w-md' : 'text-xs max-w-[280px]'}`}>
                  Ask questions about enrollment, spending, members, and more.
                  Powered by governed AI with real-time Redshift data.
                </p>
                <div className={`space-y-2 ${isExpanded ? 'max-w-lg w-full' : 'w-full'}`}>
                  {STARTER_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      onClick={() => handleSend(q)}
                      disabled={isLoading}
                      className={`w-full text-left px-3 py-2.5 text-gray-600 bg-gray-50 border border-gray-200
                                 rounded-lg hover:bg-amber-50 hover:border-amber-300 hover:text-amber-800
                                 transition-colors disabled:opacity-50
                                 ${isExpanded ? 'text-sm py-3' : 'text-xs'}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, i) => (
                  <SageChatMessage key={i} {...msg} />
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
        </div>

        {/* Input */}
        <div className={isExpanded ? 'max-w-3xl mx-auto w-full' : ''}>
          <SageChatInput onSend={handleSend} isLoading={isLoading} focusTrigger={isExpanded} />
        </div>
      </div>
    </>
  );
}
